/**
=========================================================
* Material Kit 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base typography styles for the Material Kit 2 PRO React.
 * You can add new typography style using this file.
 * You can customized the typography styles for the entire Material Kit 2 PRO React using thie file.
 */

// Material Kit 2 PRO React Base Styles
import colors from 'assets/theme/base/colors';

// Material Kit 2 PRO React Helper Functions
import pxToRem from 'assets/theme/functions/pxToRem';

const { text, black, green } = colors;

const baseProperties = {
  fontFamily: 'Inter',
  fontWeightLighter: 100,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(30),
};

const baseHeadingProperties = {
  color: black.main,
  fontWeight: baseProperties.fontWeightBold,
};

const baseDisplayProperties = {
  fontFamily: baseProperties.fontFamily,
  color: black.main,
  fontWeight: baseProperties.fontWeightLight,
  lineHeight: 1.2,
};

const typography = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLighter: 100,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: 24,
    lineHeight: '48px',
    ...baseHeadingProperties,
  },

  h2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: 18,
    lineHeight: '40px',
    ...baseHeadingProperties,
  },

  h3: {
    fontFamily: baseProperties.fontFamily,
    fontSize: 16,
    lineHeight: '16px',
    ...baseDisplayProperties,
  },

  tableData: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '16px',
    color: black.main,
  },

  tableColumnHeader: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '16px',
    color: 'rgba(0, 0, 0, 0.5)',
  },

  cardText: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '16.94px',
    color: black.main,
  },

  cardSubTitle: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 300,
    fontSize: 12,
    lineHeight: '14.52px',
    color: black.main,
  },

  formFilledText: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 300,
    fontSize: 12,
    lineHeight: '14.52px',
    color: black.main,
  },

  formTitle: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 400,
    fontSize: 10,
    lineHeight: '12.1px',
    color: text.unSelected,
  },

  navSelected: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '19.36px',
    color: green.default,
  },

  navRegular: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '19.36px',
    color: text.unSelected,
  },

  b1Button: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 16,
    color: green.main,
    letter: '0.7px',
  },

  b2Button: {
    fontFamily: baseProperties.fontFamily,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '16px',
    color: text.unSelected,
    letter: '0.7px',
  },

  h4: {
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(16),
    lineHeight: 1.375,
    color: text.regular,
    ...baseHeadingProperties,
  },

  h5: {
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(14),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h6: {
    fontFamily: baseProperties.fontFamily,
    fontSize: pxToRem(12),
    lineHeight: 1.625,
    ...baseHeadingProperties,
  },

  subtitle1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.625,
  },

  subtitle2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
  },

  body1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.625,
  },

  body2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.5,
    textTransform: 'uppercase',
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.25,
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
    '2xl': baseProperties.fontSize2XL,
    '3xl': baseProperties.fontSize3XL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },
};

export default typography;
